<template>
  <div>
    <ImpersonationBanner />

    <div
      v-if="isDashboardNotificationVisible"
      class="dashboard__notification d-flex justify-content-center align-items-center px-3"
    >
      <div class="ml-auto">
        <span class="mr-1">{{ dashboardNotificationMessageDetails.text }}</span>
        <router-link
          v-if="dashboardNotificationMessageDetails.linkText && dashboardNotificationMessageDetails.routeName && isDashboardNotificationLinkAllowed"
          :to="{ name: dashboardNotificationMessageDetails.routeName }"
          @click.native="dashboardNotificationMessageDetails.hideOnClick && hideDashboardNotification"
        >
          <u>{{ dashboardNotificationMessageDetails.linkText }}</u>
        </router-link>
      </div>

      <font-awesome-icon
        role="button"
        :icon="['fas', 'times']"
        class="ml-auto"
        :class="{'invisible': !dashboardNotificationMessageDetails.hideOnClick}"
        @click="hideDashboardNotification"
      />
    </div>

    <TheHeader />

    <TheSidebar />

    <section
      class="dashboard__section"
      :class="{ 'dashboard__section--expanded': !isSidebarExpanded }"
    >
      <slot />
    </section>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Clarity from '@microsoft/clarity'

import TheHeader from '@/components/TheHeader.vue'
import TheSidebar from '@/components/TheSidebar.vue'
import ImpersonationBanner from '@/components/ImpersonationBanner.vue'
import { isRouteAllowedByPermission } from '@/router/permissions'

export default {
  name: 'LayoutDashboard',

  components: {
    TheHeader,
    TheSidebar,
    ImpersonationBanner
  },

  computed: {
    ...mapState([
      'isSidebarExpanded',
      'isDashboardNotificationVisible',
      'dashboardNotificationMessageDetails'
    ]),

    ...mapGetters('auth', ['accountDetails', 'userId', 'hasPermission']),
    hasCampaignAccess() {
      return this.hasPermission('campaigns')
    },

    ...mapGetters('softphone', [
      'isSoftphoneAvailable',
      'isSoftphoneOwner',
      'isCallActive'
    ]),

    // The banner's link is left off when it points at a page this member may not open - "Click here
    // to add funds" for a member without the billing permission would only bounce them to the
    // dashboard. The message itself stays: it is still true, and an admin can act on it.
    isDashboardNotificationLinkAllowed() {
      const routeName = this.dashboardNotificationMessageDetails?.routeName

      if (!routeName || !this.$router) {
        return true
      }

      return isRouteAllowedByPermission(this.$router.resolve({ name: routeName }).route, this.hasPermission)
    }
  },

  async created () {
    const debug = process.env.NODE_ENV !== 'production'

    if (!debug) {
      this.initClarity()
    }

    await this.startSoftphoneIfAvailable()
  },

  watch: {
    hasCampaignAccess(available) {
      if (available) this.startSoftphoneIfAvailable()
    }
  },

  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload)
    window.addEventListener('pagehide', this.onPageHide)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    window.removeEventListener('pagehide', this.onPageHide)
  },

  methods: {
    ...mapActions(['hideDashboardNotification']),

    ...mapActions('globalSettings', ['getSoftphoneGoesLiveSetting']),

    ...mapActions('softphone', ['initSoftphone', 'teardownSoftphone']),

    /**
     * Boots the softphone for this tab.
     *
     * The setting is fetched HERE rather than left to the campaigns list, because the softphone now
     * belongs to every dashboard page - and because the gate has to be settled before the tab joins
     * the election for the ownership lock. Without that, every seller and every non-agent buyer
     * would hold a softphone lock and a BroadcastChannel for the life of their tab.
     *
     * Every dashboard tab of a softphone agent is a candidate owner and exactly one of them wins -
     * see `services/softphone/softphoneOwnership.js`. The losers mirror the winner, which is what
     * lets a campaigns list in any tab show live call state it does not own.
     *
     * Fails closed, like the rest of the softphone gate: a setting that could not be read leaves
     * the softphone off rather than half-started.
     */
    async startSoftphoneIfAvailable() {
      if (!this.hasPermission('campaigns')) return
      try {
        await this.getSoftphoneGoesLiveSetting()
      } catch (error) {
        console.error('Failed to read the softphone setting', error)

        return
      }

      if (!this.isSoftphoneAvailable) {
        return
      }

      await this.initSoftphone()
    },

    /**
     * Asks before throwing away a call that is ANSWERED. Closing the tab or reloading it ends that
     * call outright: the media lives in this tab and cannot follow the softphone lock to another
     * one, so there is no handover to save it.
     *
     * A merely RINGING call is deliberately not worth asking about, and asking actively harmed it.
     * The prompt is a modal dialog, and a modal dialog blocks this tab's main thread for as long as
     * it is up - so the websocket goes unserviced and the ring window burns down behind it. Nobody
     * has picked the call up either way, so there is nothing for the agent to save by staying: the
     * prompt could only ever cost them the call it was supposed to protect.
     *
     * The confirm is ALL this does. `beforeunload` can be cancelled, and tearing down here would
     * hang up on a caller for an agent who then clicked "stay" - the teardown belongs in
     * `pagehide`, which only fires once the page really is going.
     */
    onBeforeUnload(event) {
      if (!this.isSoftphoneOwner || !this.isCallActive) {
        return undefined
      }

      event.preventDefault()

      // Browsers show their own wording and ignore this string; it is set because the older ones
      // treat its presence as the request to prompt at all.
      event.returnValue = 'A call is in progress. Leaving this page will end it.'

      return event.returnValue
    },

    /**
     * The page is really going. Pauses the campaign, drops the transport, says goodbye to the other
     * tabs and releases the lock, in that order - see `teardownSoftphone`.
     *
     * `pagehide` rather than `beforeunload` because it is the more reliable of the two (mobile
     * Safari in particular fires only this one), and `persisted` tells us the difference between an
     * unload and a trip into the back/forward cache - a cached page comes back with its store
     * intact and must not have torn anything down.
     */
    onPageHide(event) {
      if (event.persisted) {
        return
      }

      this.teardownSoftphone({ isUnloading: true })
    },

    initClarity() {
      try {
        Clarity.identify(this.userId, '', '', this.accountDetails.name) // No need to mention page Id here
      } catch (error) {
        console.error('Error calling Clarity Identify API', error)
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.dashboard__section {
  margin-left: 180px;
  padding-top: 55px;
}

.dashboard__section--expanded {
  margin-left: 60px;
}

.dashboard__notification {
  position: fixed;
  z-index: $zindex-tooltip;
  top: 0;
  left: 0;
  right: 0;
  background: #{$danger}f5;
  color: $white;
  line-height: 2.5;
  overflow: hidden; 
  box-shadow: 0 0 5px black;

  animation: slideDown 2.5s 1s 1 ease forwards;
  transform: translateY(-50px);
}
</style>
